import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong, faPlus } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import {
  getBillingGroup,
  updateBillingGroup,
} from "../../api/BillingGroups";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import EditForm from "../../components/EditForm";
import Breadcrumbs from "../../components/Breadcrumbs";

const EditBillingGroupPage = () => {
  const { getAccessToken } = useAuth();
  const { billingGroupPublicId, agreementPublicId } = useParams();
  
  const navigate = useNavigate();
  const [billingGroup, setBillingGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [billingGroups, setBillingGroups] = useState([]);

  useEffect(() => {
    const fetch = async () => {
      try {
        const accessToken = await getAccessToken();
        const result = await getBillingGroup(billingGroupPublicId, accessToken);
        setBillingGroup(result);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load billing group");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [billingGroupPublicId]);

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: "Discard changes?",
        confirm: () => {
          navigate(-1);
        },
      }));
    } else {
      navigate(-1);
    }
  };

  const handleSave = async () => {
    try {
      const accessToken = await getAccessToken();
  
      const updatePayload = {
        agreementPublicId: agreementPublicId || "",
        name: billingGroup.name,
        status: billingGroup.status,
        comment: billingGroup.comment,
        billingGroupPublicId,
        updatedDateTimeUTC: new Date().toISOString(), // aktuellt klockslag
        isDefault: billingGroup.isDefault,
      };
  
      console.log("🧾 Sending update payload:", updatePayload);

      await updateBillingGroup(updatePayload, accessToken);
  
      toast.success("Billing group updated", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
      
      setHasChanges(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update billing group");
    }
    navigate(-1);
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage title={"BillingGroups"} loading={loading} />
          </div>
          <div className={`${loading ? "loading" : ""} page-content`}>
            <Breadcrumbs />

            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                Edit billing group: {billingGroup?.name || "Unknown"}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button className="btn btn-outline-primary" onClick={handleBackClick}>
                  Cancel
                </button>
                <button className="btn btn-primary" disabled={!hasChanges} onClick={handleSave}>
                Save
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <EditForm
                data={billingGroup}
                setData={setBillingGroup}
                setHasChanges={setHasChanges}
                disabled={false}
                fields={[
                  {
                    key: "isDefault",
                    label: "Default billing group",
                    type: "switch",
                    disabled: false,
                  },
                  {
                    key: "billingGroupPublicId",
                    label: "Billing group ID",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "name",
                    label: "Name",
                    type: "text",
                    disabled: false,
                  },
                  {
                    key: "status",
                    label: "Status",
                    type: "dropdown",
                    options: [
                      { value: "Active", label: "Active" },
                      { value: "Inactive", label: "Inactive" },
                    ],
                  },
                  {
                    key: "comment",
                    label: "Comment",
                    type: "text",
                    disabled: false,
                  },
                  {
                    key: "createdBy",
                    label: "Created by",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "createdDateTimeUTC",
                    label: "Created date",
                    type: "date",
                    disabled: true,
                  },
                  {
                    key: "updatedBy",
                    label: "Updated by",
                    type: "text",
                    disabled: true,
                  },
                  {
                    key: "updatedDateTimeUTC",
                    label: "Updated date",
                    type: "date",
                    disabled: true,
                  },
                ]}
                dropdownOptions={{
                  billingGroupName: billingGroups.map((g) => ({
                    name: g.name,
                    billingGroupPublicId: g.billingGroupPublicId,
                  })),
                }}
              />
            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title ? confirmState.title : "Discard changes?"}
          onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default EditBillingGroupPage;
